// WSJF with uncertainty: weighted shortest job first ranks jobs by cost of delay over
// job size (Reinertsen; SAFe splits cost of delay into business value, time
// criticality and risk reduction or opportunity enablement). Here each score is a
// range: the three cost-of-delay parts are uniform on their ranges (relative scores),
// size is lognormal on its 90% range. Simulation gives how often each job takes each
// place. Source: docs/backlog-prioritization-research.md#52-wsjf-and-cost-of-delay.
// Tests: model.test.js.

import { Rng } from "../lib/rng.js";
import { drawLognormal, drawUniform, lognormalFrom90, meanOf } from "../lib/ranges.js";
import { orderBy, rankProbabilities } from "../lib/rankprob.js";

export const DRAWS = 5000;
export const SEED = 20261015;
export const MAX_JOBS = 30;
export const PARTS = ["value", "time", "risk"];
export const PART_LABELS = { value: "Business value", time: "Time criticality", risk: "Risk reduction or opportunity" };
/** A place held at least this often is "settled"; two neighbours within 40–60% of each other are a coin flip. */
export const SETTLED = 0.8;

/**
 * Problems with one job, in words; empty when usable.
 * @param {{ value: {low:number,high:number}, time: {low:number,high:number}, risk: {low:number,high:number}, size: {low:number,high:number} }} job
 * @returns {string[]}
 */
export function jobIssues(job) {
  const out = [];
  for (const k of [...PARTS, "size"]) {
    const r = job[k];
    const name = k === "size" ? "Job size" : PART_LABELS[k];
    if (!(Number.isFinite(r.low) && Number.isFinite(r.high))) out.push(`${name} needs both ends.`);
    else if (r.low > r.high) out.push(`${name} runs backwards.`);
    else if (r.low < 0) out.push(`${name} is below 0.`);
  }
  if (Number.isFinite(job.size.low) && job.size.low === 0) out.push("Job size must be above 0.");
  return out;
}

/** WSJF from the middle of each range: what a single-number scoring session would write. */
export function pointWsjf(job) {
  const cod = PARTS.reduce((s, k) => s + (job[k].low + job[k].high) / 2, 0);
  return cod / Math.sqrt(job.size.low * job.size.high);
}

/**
 * Rank the jobs under uncertainty.
 * @param {{ jobs: object[], top: number }} input
 * @param {{ draws?: number, seed?: number }} [opts]
 * @returns {object}
 */
export function analyseWsjf(input, { draws = DRAWS, seed = SEED } = {}) {
  const usable = [];
  const excluded = [];
  input.jobs.forEach((job, index) => {
    const issues = jobIssues(job);
    if (issues.length) excluded.push({ index, label: job.label, reasons: issues });
    else usable.push({ ...job, index, sizeD: job.size.high > job.size.low ? lognormalFrom90(job.size.low, job.size.high) : null });
  });
  if (!usable.length) return { jobs: [], excluded, empty: true };

  const rng = new Rng(seed);
  const scores = [];
  const sums = new Float64Array(usable.length);
  for (let d = 0; d < draws; d++) {
    const row = usable.map((j) => {
      const cod = PARTS.reduce((s, k) => s + drawUniform(rng, j[k].low, j[k].high), 0);
      const size = j.sizeD ? drawLognormal(rng, j.sizeD) : j.size.low;
      return cod / size;
    });
    row.forEach((x, i) => (sums[i] += x));
    scores.push(row);
  }
  const ranks = rankProbabilities(scores);
  const expected = Array.from(sums, (s) => s / draws);
  const order = orderBy(expected);
  const top = Math.max(1, Math.min(usable.length, Math.round(input.top || 1)));
  const jobs = order.map((i, place) => {
    const j = usable[i];
    const next = order[place + 1];
    const aheadOfNext = next == null ? null : scores.reduce((n, row) => n + (row[i] > row[next] ? 1 : 0), 0) / draws;
    return {
      index: j.index,
      label: j.label,
      place: place + 1,
      expected: expected[i],
      point: pointWsjf(j),
      pFirst: ranks[i][0],
      pPlace: ranks[i][place],
      pTop: ranks[i].slice(0, top).reduce((a, b) => a + b, 0),
      aheadOfNext,
      coinFlip: aheadOfNext != null && aheadOfNext < 0.6,
      settled: ranks[i][place] >= SETTLED,
      ranks: ranks[i],
    };
  });
  const pointOrder = orderBy(usable.map(pointWsjf)).map((i) => usable[i].index);
  return { jobs, excluded, empty: false, top, pointOrder, sameAsPoint: pointOrder.every((idx, k) => jobs[k].index === idx) };
}

export const pct = (p) => (Number.isFinite(p) ? `${Math.round(p * 100)}%` : "—");
export const fmt = (x) => (Number.isFinite(x) ? (Math.round(x * 100) / 100).toString() : "—");

/**
 * Plain sentences for a ticket, a channel or an agent's prompt.
 * @param {object} r analyseWsjf result
 * @returns {string[]}
 */
export function summaryLines(r) {
  if (r.empty) return [];
  const lines = [`Order by expected WSJF: ${r.jobs.map((j) => `${j.place}. ${j.label}`).join("; ")}.`];
  const first = r.jobs[0];
  lines.push(`${first.label} comes first in ${pct(first.pFirst)} of the scorings the ranges allow.`);
  const flips = r.jobs.filter((j) => j.coinFlip).map((j) => `${j.label} vs ${r.jobs[j.place].label}`);
  if (flips.length) lines.push(`Close calls, ahead less than 60% of the time: ${flips.join("; ")}. Settle these by discussing the scores, not by the ranking.`);
  lines.push("Scores are relative and read as ranges: uniform for the cost-of-delay parts, a 90% range for size.");
  return lines;
}
